import React from 'react';
import { Link } from 'react-router-dom';
import { Scale, ArrowRight } from 'lucide-react';
import AltHeader from '../components/AltHeader';
import AltFooter from '../components/AltFooter';
import AltParallaxBackdrop from '../components/AltParallaxBackdrop';

/**
 * AffiliateTerms
 *
 * The legal / program-rules terms affiliates agree to when they submit
 * the apply form at /affiliate. Short, plain-language, covers the ground
 * the user explicitly asked about: honest promotion, disclosure, anti-
 * self-referral, payout rules, and Levanta's right to remove bad actors.
 */
const AffiliateTerms = () => {
  return (
    <div className="bg-white overflow-x-hidden">
      <AltHeader />

      {/* HERO */}
      <section className="relative pt-[110px] md:pt-[170px] pb-14 md:pb-24 px-6 overflow-hidden min-h-[600px] md:min-h-[640px]">
        <AltParallaxBackdrop />
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#0b1f3a]/80 backdrop-blur-md text-xs md:text-[13px] font-semibold tracking-[0.18em] uppercase text-white shadow-lg ring-1 ring-white/20 mb-7">
            <Scale className="w-3.5 h-3.5 text-cyan-300" strokeWidth={2.5} />
            Affiliate Program Terms
          </span>
          <h1 className="text-4xl md:text-7xl font-black leading-[1.02] tracking-tight text-[#0b1f3a] alt-hero-glow">
            The agreement between{' '}
            <em className="italic font-light text-blue-600">partners.</em>
          </h1>
          <p className="mt-8 text-lg md:text-xl text-[#0b1f3a]/75 max-w-2xl mx-auto leading-relaxed alt-hero-glow">
            Plain rules for the Levanta Affiliate Partner Program — how you get
            paid, what you commit to, and what we ask in return.
          </p>
          <p className="mt-6 text-sm font-mono text-[#0b1f3a]/55">
            Last updated · March 2026
          </p>
        </div>
      </section>

      {/* CONTENT */}
      <section className="px-6 py-20 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto prose prose-lg prose-headings:font-black prose-headings:text-[#0b1f3a] prose-h2:mt-12 prose-h2:mb-4 prose-p:text-[#0b1f3a]/80 prose-p:leading-relaxed prose-li:text-[#0b1f3a]/80 prose-strong:text-[#0b1f3a] prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline">
          <h2>1. Who these terms apply to</h2>
          <p>
            These terms apply to anyone who submits an application at
            levanta.life/affiliate, receives a unique referral code, and uses
            that code to introduce new members to the Levanta platform
            ("Affiliate", "you", "your"). "Levanta", "we", "us" and "our" refer
            to Levanta — registered in Israel as a Licensed Dealer (Osek
            Murshe), Certificate No. 316751049.
          </p>

          <h2>2. Applying &amp; activation</h2>
          <p>
            You become an Affiliate when you (a) submit the application form
            with accurate information, (b) verify ownership of the email
            address you provided by clicking the confirmation link we send
            you, and (c) agree to these terms. We may decline any application
            at our sole discretion and without providing a reason.
          </p>

          <h2>3. Your referral code</h2>
          <p>
            On successful email verification we issue a unique 8-character
            referral code tied to your affiliate account. You must not share
            the code in any way that misrepresents Levanta, or combine it with
            discounts, coupons or offers not authorised by Levanta. You may
            not transfer, sell or sub-licence your code to any third party.
          </p>

          <h2>4. Commission structure</h2>
          <p>
            Every user who applies your referral code at signup receives a 10%
            discount on their first Levanta membership payment. For each such
            user you earn:
          </p>
          <ul>
            <li>
              <strong>40% commission</strong> on the first month of a monthly
              membership;
            </li>
            <li>
              <strong>20% recurring commission</strong> on every subsequent
              monthly payment, for as long as the member stays active;
            </li>
            <li>
              <strong>30% one-time commission</strong> on each annual
              membership purchase.
            </li>
          </ul>
          <p>
            Commissions are calculated on the net amount actually paid by the
            member to Levanta, after any applicable discounts (including the
            10% referral discount), taxes, refunds and chargebacks.
          </p>

          <h2>5. Payouts</h2>
          <ul>
            <li>
              Minimum payout threshold: <strong>$20 USD</strong>. Earnings
              below the threshold accumulate until it is reached.
            </li>
            <li>
              Payment schedule: monthly, on or around the 1st of each month for
              the previous month's verified earnings.
            </li>
            <li>Payment method: PayPal or bank transfer, as selected by you.</li>
            <li>
              If a member cancels, commission stops from the month of
              cancellation. Previously-paid commissions are never reversed.
            </li>
            <li>
              Refunded or charged-back subscription payments do not generate
              commission. If a commission has already been paid on such a
              payment, it may be deducted from a future payout.
            </li>
          </ul>

          <h2>6. What you must do</h2>
          <ul>
            <li>
              Disclose the affiliate relationship in every piece of
              promotional content (#ad, #partner or #affiliate). This is
              required by FTC guidelines in the US and equivalent rules in
              most other jurisdictions.
            </li>
            <li>
              Represent Levanta accurately — share what the platform actually
              does, not exaggerated claims.
            </li>
            <li>
              Only promote Levanta to audiences who would genuinely benefit
              from a structured personal growth system.
            </li>
            <li>Use your unique code so your referrals are tracked correctly.</li>
          </ul>

          <h2>7. What you must not do</h2>
          <ul>
            <li>
              Promise specific income, earnings, health, emotional, spiritual
              or other results to your audience on Levanta's behalf.
            </li>
            <li>
              Run paid advertising that uses Levanta's name, trademarks, or
              your referral code without our prior written approval.
            </li>
            <li>
              Self-refer — creating Levanta accounts through your own referral
              code is prohibited and will result in immediate removal from the
              program and forfeiture of all unpaid commissions.
            </li>
            <li>
              Use misleading, false, or deceptive claims about Levanta's
              features, pricing, or results.
            </li>
            <li>
              Use Levanta's brand, logos or copyrighted material outside the
              promotion kits we provide, without our prior written approval.
            </li>
            <li>
              Engage in spam, cookie-stuffing, trademark bidding or any other
              practice commonly considered abusive in affiliate marketing.
            </li>
          </ul>

          <h2>8. Levanta reserves the right</h2>
          <ul>
            <li>
              To remove any Affiliate from the program for violation of these
              terms.
            </li>
            <li>
              To adjust commission rates with 30 days' written notice to
              existing Affiliates.
            </li>
            <li>To reject any application without providing a reason.</li>
            <li>
              To deactivate a referral code at any time if it is used
              fraudulently.
            </li>
          </ul>

          <h2>9. Term &amp; termination</h2>
          <p>
            Either party may end the partnership at any time and for any
            reason. On termination, your referral code is disabled, new
            redemptions stop, and commissions already earned on members who
            remain active continue to be paid out on the normal schedule until
            those members cancel.
          </p>

          <h2>10. Independent contractor</h2>
          <p>
            You participate in the Affiliate Program as an independent
            contractor. Nothing in these terms creates a partnership, joint
            venture, employment or agency relationship between you and
            Levanta. You are responsible for all taxes, fees and filings
            related to commissions you earn.
          </p>

          <h2>11. Confidentiality &amp; data</h2>
          <p>
            Non-public information you receive from us — pricing roadmaps,
            unreleased features, internal metrics, contact lists — is
            confidential. We process your personal data according to our{' '}
            <Link to="/privacy">privacy notice</Link>.
          </p>

          <h2>12. Limitation of liability</h2>
          <p>
            To the fullest extent permitted by law, Levanta's total liability
            under these terms is limited to the total commissions paid to you
            in the twelve months preceding the claim. Levanta is not liable
            for indirect, incidental, consequential or special damages.
          </p>

          <h2>13. Governing law</h2>
          <p>
            These terms are governed by the laws of the State of Israel,
            without regard to its conflict-of-law rules. Disputes that cannot
            be resolved informally will be submitted to the exclusive
            jurisdiction of the competent courts of Israel — except that
            either party may seek injunctive relief to protect intellectual
            property in any court with jurisdiction.
          </p>

          <h2>14. Changes to these terms</h2>
          <p>
            We may update these terms from time to time. Material changes will
            be communicated by email to your registered address and will take
            effect no sooner than 14 days after that notice. Your continued
            use of your referral code after the effective date constitutes
            acceptance of the updated terms.
          </p>

          <h2>15. Contact</h2>
          <p>
            Questions about these terms or about the program in general:{' '}
            <a href="mailto:business@levanta.life">business@levanta.life</a>.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 py-20 md:py-24 bg-[#0b1f3a]">
        <div className="max-w-3xl mx-auto text-center text-white">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-300 mb-4">
            Ready to partner?
          </p>
          <h2 className="text-3xl md:text-5xl font-black leading-tight tracking-tight">
            Apply for your{' '}
            <em className="italic font-light text-blue-300">affiliate code.</em>
          </h2>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link
              to="/affiliate"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-white text-[#0b1f3a] font-semibold shadow-md hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-200"
            >
              Apply now
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-white/5 text-white font-semibold ring-1 ring-white/20 hover:bg-white/10 transition-all duration-200"
            >
              Contact us
            </Link>
          </div>
        </div>
      </section>

      <AltFooter />
    </div>
  );
};

export default AffiliateTerms;
